import * as React from 'react';
import Button from 'src/components/Button';
import Input from 'src/components/FormElements/Input';
import Textarea from 'src/components/FormElements/Textarea';
import Icon from 'src/components/Icon';
import { Column, Columns } from 'src/components/Layout/Grid';
import ImageSelect from './ImageSelect';
import { IValidationErrors } from 'src/utils/validation';
import FieldError from 'src/components/FormElements/FieldError';
import { ITheme } from 'src/Entities/Theme';
import { addToast } from 'src/components/Toast';

export interface IThemeSectionFormValues {
  name: string;
  description: string;
  buttonText: string;
  imageUrl: string | null;
  image: File | null;
}

export interface IThemeSectionFormProps {
  onSubmit: (values: IThemeSectionFormValues) => Promise<any>;
  onCancel: () => void;
  theme: ITheme;
}

const validate = (
  values: IThemeSectionFormValues
): null | IValidationErrors => {
  const errors: IValidationErrors = {};
  let valid = true;

  if (!values.name.trim()) {
    valid = false;
    errors.name = 'er påkrævet';
  }

  if (!values.description.trim()) {
    valid = false;
    errors.description = 'er påkrævet';
  }

  if (!values.buttonText.trim()) {
    valid = false;
    errors.buttonText = 'er påkrævet';
  } else {
    const buttonTextLength = values.buttonText
      .replace(/(\{antal kurser\})/, 'X')
      .length;

    if (buttonTextLength > 40) {
      valid = false;
      errors.buttonText = `må ikke indeholde mere end 40 tegn`;
    }
  }

  if (!values.image && !values.imageUrl) {
    valid = false;
    errors.image = 'er påkrævet';
  }

  return valid ? null : errors;
};

const ThemeSectionForm = ({
  onSubmit,
  onCancel,
  theme
}: IThemeSectionFormProps) => {
  const themeRef = React.useRef<ITheme>(theme);
  const [errors, setErrors] = React.useState<IValidationErrors>({});
  const [values, setValues] = React.useState<IThemeSectionFormValues>(() => ({
    name: theme.name,
    description: theme.description,
    buttonText: theme.buttonText || '',
    imageUrl: theme.themeImageUrl,
    image: null
  }));
  const buttonTextLength = React.useMemo(() => {
    return values.buttonText
      .replace(/(\{antal kurser\})/, 'X')
      .length;
  }, [values.buttonText]);
  const previewButtonContent = React.useMemo(() => {
    const fragments = values.buttonText.trim().split(/(\{antal kurser\})/);
    const content = [];
    let interpolationIndex = -1;

    for (const fragment of fragments) {
      if (fragment === '{antal kurser}') {
        content.push(
          <span key={++interpolationIndex} className="b mx-1">
            10
          </span>
        );
      } else if (fragment) {
        content.push(fragment);
      }
    }

    return content;
  }, [values.buttonText]);

  React.useEffect(() => {
    if (theme.themeId !== themeRef.current.themeId) {
      setValues({
        name: theme.name,
        description: theme.description,
        buttonText: theme.buttonText ?? '',
        imageUrl: theme.themeImageUrl,
        image: null
      });
      setErrors({});
    }
    themeRef.current = theme;
  }, [theme]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { target } = event;
    setValues({ ...values, [target.name]: target.value });
  };

  const handleRemoveImage = () => {
    setValues({ ...values, imageUrl: null });
  };

  const handleImageFileChange = (image: File | null) => {
    setValues({ ...values, image });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const validationErrors = validate(values);
    setErrors(validationErrors || {});
    if (validationErrors) {
      addToast({
        type: 'error',
        message: 'Formularen blev ikke udfyldt korrekt'
      });
      return;
    }

    onSubmit(values);
  };

  return (
    <form className="p-14" onSubmit={handleSubmit}>
      <div className="mb-6">
        <label htmlFor="name" className="font-bold f5 uppercase block mb-1">
          Sektionstitel
        </label>
        <Input
          type="text"
          name="name"
          id="name"
          variant="inset"
          elementSize="large"
          className="w-full block"
          autoComplete="off"
          value={values.name}
          showErrorBorder={errors.name !== undefined}
          onChange={handleChange}
          autoFocus={true}
        />
        {errors.name && <FieldError className="mt-1">{errors.name}</FieldError>}
      </div>
      <Columns gutter="default">
        <Column md={6}>
          <div className="mb-6">
            <label
              htmlFor="description"
              className="font-bold f5 uppercase block mb-1"
            >
              Beskrivelse (max 200 tegn)
            </label>
            <Textarea
              name="description"
              id="description"
              variant="inset"
              elementSize="large"
              rows={6}
              maxLength={200}
              value={values.description}
              showErrorBorder={errors.description !== undefined}
              onChange={handleChange}
            />
            <div className="flex justify-between">
              {errors.description && (
                <FieldError className="mt-1">{errors.description}</FieldError>
              )}
              <div className="f6 has-text-gray-700 mt-1 font-bold ml-auto">
                {`Tegn brugt: ${values.description.length || 0}/200`}
              </div>
            </div>
          </div>
          <div className="mb-6">
            <label
              htmlFor="buttonText"
              className="font-bold f5 uppercase block mb-1"
            >
              Tekst på knap (max 40 tegn)
            </label>
            <div className="f5 has-text-gray-700 mb-2">
              {'Værdien '}
              <code className="has-text-gray-900 has-background-gray-300 i">
                {'{antal kurser}'}
              </code>
              {
                ' bliver erstattet med det antal kurser, der blev fundet i temaet.'
              }
            </div>
            <Input
              type="text"
              name="buttonText"
              id="buttonText"
              variant="inset"
              elementSize="large"
              className="w-full block"
              autoComplete="off"
              value={values.buttonText}
              showErrorBorder={errors.buttonText !== undefined}
              onChange={handleChange}
            />
            <div className="flex">
              {errors.buttonText && (
                <FieldError className="mt-1">{errors.buttonText}</FieldError>
              )}
              <div className="f6 has-text-gray-700 mt-1 font-bold ml-auto">
                {`Tegn brugt: ${buttonTextLength || 0}/40`}
              </div>
            </div>
            <div className="font-bold f5 uppercase block mt-2 mb-1">
              Eksempel på knap
            </div>
            <div className="has-background-gray-300 p-4 text-center">
              <Button type="button" color="gray">
                {previewButtonContent.length === 0 ? (
                  <span className="i">Igen tekst</span>
                ) : (
                  previewButtonContent
                )}
              </Button>
            </div>
          </div>
        </Column>
        <Column md={6}>
          <label className="font-bold f5 uppercase block mb-1">
            Billede brugt til sektionen
          </label>
          <ImageSelect
            imageFile={values.image}
            imageUrl={values.imageUrl}
            showErrorBorder={errors.image !== undefined}
            onRemoveImage={handleRemoveImage}
            onImageFileChange={handleImageFileChange}
          />
          {errors.image && (
            <FieldError className="mt-1">{errors.image}</FieldError>
          )}
        </Column>
      </Columns>
      <div className="flex justify-end mt-6">
        <Button type="button" color="gray" onClick={onCancel}>
          Fortryd
        </Button>
        <Button className="ml-2">
          Opdater
          <Icon name="refresh" className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default ThemeSectionForm;



// WEBPACK FOOTER //
// ./src/Pages/CoursePortal/ThemeSectionEditor/components/ThemeSectionForm.tsx